import { ReadonlyVec3 } from 'gl-matrix';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { Environment } from '~/engine/data/Environment';
import { useContourLayerViewUpdate } from './useContourLayerViewUpdate';

interface UsePreviewBaseOptions {
    environment: Environment | null;
}

export interface PreviewBaseResult {
    isPreviewing: boolean;
    setIsPreviewing: Dispatch<SetStateAction<boolean>>;
    /**
     * Raise the busy flag for a new preview run and return that run's id.
     * Pair with {@link endPreviewRun} in a `finally`.
     */
    beginPreviewRun: () => number;
    /**
     * Lower the busy flag, but ONLY when `runId` is still the latest run.
     *
     * Two Preview clicks can both land before the disabled state repaints
     * (a double click, or a click while the busy re-render is still
     * pending), starting a second run while the first is inside its
     * compute. The first is superseded — {@link beginOperation} has
     * aborted its signal and its abort paths keep it from publishing — but
     * clearing the flag from its `finally` would re-enable Apply and lapse
     * the panel's close prevention while the second run is still on the
     * GPU. Busy therefore stays true until the LAST run settles.
     */
    endPreviewRun: (runId: number) => void;
    updateView: () => void;
    /** Start a new abortable operation. Returns the signal to check inside async work. */
    beginOperation: () => AbortSignal;
    /**
     * Publish a wrapper to the preview slot, disposing any previously-held
     * wrapper. `key` is optional — supply it only when the caller intends to
     * later hand off the texture via `consumeResult`.
     */
    setResult: (previewObj: CustomTargetObject, key?: string) => void;
    /** Hand off the held result's texture if its key matches the requested key. */
    consumeResult: (key: string) => ContourTexture | undefined;
    /**
     * Repaint the held preview in a new color — render-only, so the held
     * key (and any staleness derived from it) is untouched. No-op while
     * no result is held.
     */
    recolorResult: (color: ReadonlyVec3) => void;
    /**
     * Show or hide the held result WITHOUT disposing it, so a caller can
     * flip between the structure as it stands and as the operation would
     * leave it. Recomputing to come back would make the comparison cost a
     * GPU pass each way, and the two pictures have to be of the same
     * computation to be worth comparing at all.
     *
     * Sticky: a recompute while hidden publishes nothing, so the result
     * arrives held rather than drawn.
     */
    setResultVisible: (visible: boolean) => void;
    /** Abort any in-flight compute without touching the held result. */
    abortInFlight: () => void;
    /** Abort any in-flight compute and dispose the held result. */
    cleanup: () => void;
    /**
     * Key of the preview ACTUALLY held right now (null when none, or when
     * it was published without a key). Kept in lockstep with the held
     * result itself, so staleness derived from it can never claim a
     * preview that a failure path has already destroyed — unlike a
     * caller-side mirror updated only on the success path.
     */
    heldKey: string | null;
    /** Read the held preview without taking ownership of it. */
    peekResult: (
        key: string
    ) => { texture: ContourTexture; color: ReadonlyVec3 } | undefined;
    /** Monotonic, bumped whenever the held result changes. */
    resultRevision: number;
}

/** Result-lifecycle infrastructure shared by SDF-operation preview hooks. */
export function usePreviewBase({
    environment,
}: UsePreviewBaseOptions): PreviewBaseResult {
    const [isPreviewing, setIsPreviewing] = useState(false);
    // Monotonic id of the latest started preview run — see endPreviewRun.
    const latestRunIdRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const resultRef = useRef<{
        key?: string;
        previewObj: CustomTargetObject;
    } | null>(null);
    // State mirror of resultRef's key, so consumers can render staleness.
    // Updated wherever resultRef is — never independently.
    const [heldKey, setHeldKey] = useState<string | null>(null);
    const [resultRevision, setResultRevision] = useState(0);

    const updateView = useContourLayerViewUpdate(environment);

    const invalidateResult = useCallback(() => {
        const result = resultRef.current;
        if (!result) {
            return;
        }
        if (environment?.operationPreviewStructure === result.previewObj) {
            environment.operationPreviewStructure = undefined;
        }
        result.previewObj.delete();
        resultRef.current = null;
        setHeldKey(null);
        setResultRevision((revision) => revision + 1);
        updateView();
    }, [environment, updateView]);

    // Whether the held result is DRAWN. A ref, not state: `setResult`
    // reads it and must not be rebuilt when it flips, or a recompute
    // scheduled against the old identity would publish over the user's
    // choice to look at the structure instead.
    const visibleRef = useRef(true);

    const setResultVisible = useCallback(
        (visible: boolean) => {
            visibleRef.current = visible;
            const result = resultRef.current;
            if (!environment || !result) {
                return;
            }
            environment.operationPreviewStructure = visible
                ? result.previewObj
                : undefined;
            updateView();
        },
        [environment, updateView]
    );

    const setResult = useCallback(
        (previewObj: CustomTargetObject, key?: string) => {
            if (!environment) {
                invalidateResult();
                previewObj.delete();
                return;
            }
            // Swap atomically: publish the new preview, THEN dispose the old
            // wrapper, with a single updateView(). operationPreviewStructure is
            // never undefined mid-swap, so the other structures never un-dim
            // for a frame.
            const previous = resultRef.current;
            resultRef.current = { key, previewObj };
            setHeldKey(key ?? null);
            setResultRevision((revision) => revision + 1);
            // Held either way; drawn only if the caller is showing it.
            if (visibleRef.current) {
                environment.operationPreviewStructure = previewObj;
            }
            if (previous && previous.previewObj !== previewObj) {
                previous.previewObj.delete();
            }
            updateView();
        },
        [environment, invalidateResult, updateView]
    );

    const consumeResult = useCallback(
        (key: string): ContourTexture | undefined => {
            const result = resultRef.current;
            if (!result || result.key !== key) {
                return undefined;
            }
            const texture = result.previewObj.contourTexture;
            if (environment?.operationPreviewStructure === result.previewObj) {
                environment.operationPreviewStructure = undefined;
            }
            resultRef.current = null;
            setHeldKey(null);
            setResultRevision((revision) => revision + 1);
            updateView();
            return texture;
        },
        [environment, updateView]
    );

    const peekResult = useCallback(
        (
            key: string
        ): { texture: ContourTexture; color: ReadonlyVec3 } | undefined => {
            const result = resultRef.current;
            const texture = result?.previewObj.contourTexture;
            if (!result || result.key !== key || !texture) {
                return undefined;
            }
            return { texture, color: result.previewObj.color };
        },
        []
    );

    const recolorResult = useCallback(
        (color: ReadonlyVec3) => {
            const result = resultRef.current;
            if (!result) {
                return;
            }
            result.previewObj.color = color;
            updateView();
        },
        [updateView]
    );

    const beginPreviewRun = useCallback((): number => {
        const runId = ++latestRunIdRef.current;
        setIsPreviewing(true);
        return runId;
    }, []);

    const endPreviewRun = useCallback((runId: number) => {
        if (latestRunIdRef.current === runId) {
            setIsPreviewing(false);
        }
    }, []);

    const abortInFlight = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    const cleanup = useCallback(() => {
        abortInFlight();
        invalidateResult();
    }, [abortInFlight, invalidateResult]);

    const beginOperation = useCallback((): AbortSignal => {
        // Abort any in-flight compute, but KEEP the currently-displayed
        // preview on screen until its replacement is ready. `setResult`
        // disposes the old wrapper and publishes the new one atomically
        // (single `updateView`), so there is no frame without a preview.
        // Calling `cleanup()` here instead would clear
        // `operationPreviewStructure` for the whole async recompute window,
        // which un-dims the other structures and reads as a flicker.
        abortInFlight();
        const controller = new AbortController();
        abortRef.current = controller;
        return controller.signal;
    }, [abortInFlight]);

    // Abort any in-flight compute and dispose any still-held result when
    // the consumer unmounts or the bound Environment changes: a result
    // belongs to the image it was computed on. `cleanup` follows the
    // Environment alone, so a change runs the previous one, which clears
    // the slot of the Environment the result was published on.
    useEffect(() => cleanup, [cleanup]);

    return useMemo(
        () => ({
            isPreviewing,
            setIsPreviewing,
            beginPreviewRun,
            endPreviewRun,
            updateView,
            beginOperation,
            setResult,
            setResultVisible,
            consumeResult,
            recolorResult,
            abortInFlight,
            cleanup,
            heldKey,
            peekResult,
            resultRevision,
        }),
        [
            isPreviewing,
            setIsPreviewing,
            beginPreviewRun,
            endPreviewRun,
            updateView,
            beginOperation,
            setResult,
            setResultVisible,
            consumeResult,
            recolorResult,
            abortInFlight,
            cleanup,
            heldKey,
            peekResult,
            resultRevision,
        ]
    );
}
